import {
  ArrowUpRight,
  Boxes,
  Check,
  Download,
  GitBranch,
  History,
  LoaderCircle,
  Pause,
  Play,
  Rocket,
  RotateCcw,
  Save,
  ShieldCheck,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  createQodeVersion,
  getQodeDetail,
  markQodeVersionReady,
  pauseQode,
  publishQode,
  publishQodeVersion,
  requestQodeApproval,
  resumeQode,
  rollbackQode,
  updateDraftQode,
  validateQodeVersion,
} from "../api";
import type {
  DraftQodeChanges,
  QodeDetailResponse,
  QodeItem,
  QodeMutationResponse,
  TimelineEvent,
} from "../types";
import { absoluteShortUrl, formatRelativeTime } from "../lib/format";
import { downloadQrArtifact } from "../lib/qrArtifacts";
import { QrPreview } from "../components/QrPreview";

type QodeDetailDrawerProps = {
  capabilities: {
    operate: boolean;
    approve: boolean;
  };
  qode: QodeItem;
  onClose: () => void;
  onQodeChanged: (qode: QodeItem, event?: TimelineEvent | null) => void;
};

type ActionId =
  | "save"
  | "publish"
  | "request-publish"
  | "pause"
  | "resume"
  | "version-create"
  | `version-validate:${string}`
  | `version-ready:${string}`
  | `version-publish:${string}`
  | `rollback:${string}`
  | `request-rollback:${string}`;

const versionStatusLabel = {
  draft: "Draft",
  validated: "Validated",
  ready: "Ready",
  active: "Active",
  superseded: "Superseded",
} as const;

export function QodeDetailDrawer({
  capabilities,
  qode,
  onClose,
  onQodeChanged,
}: QodeDetailDrawerProps) {
  const [detail, setDetail] = useState<QodeDetailResponse | null>(null);
  const [form, setForm] = useState<DraftQodeChanges>({
    name: qode.name,
    purpose: qode.purpose,
    slug: qode.slug,
    destination: qode.destination,
  });
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState<ActionId | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [changeReason, setChangeReason] = useState("");
  const [requestedActions, setRequestedActions] = useState<Set<string>>(
    () => new Set(),
  );
  const currentQode = detail?.qode ?? qode;
  const isDraft = currentQode.status === "draft";
  const isPaused = currentQode.status === "paused";
  const canEditConfig = capabilities.operate;
  const destinationChanged = form.destination.trim() !== currentQode.destination;

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getQodeDetail(qode.id);
      setDetail(response);
      setForm({
        name: response.qode.name,
        purpose: response.qode.purpose,
        slug: response.qode.slug,
        destination: response.qode.destination,
      });
      setError("");
      setNotice("");
      setChangeReason("");
      setRequestedActions(new Set());
    } catch (loadError) {
      setError(
        loadError instanceof Error ? loadError.message : "无法读取 Qode 详情。",
      );
    } finally {
      setLoading(false);
    }
  }, [qode.id]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const refreshAfterMutation = async (response: QodeMutationResponse) => {
    onQodeChanged(response.qode, response.event);
    const fresh = await getQodeDetail(response.qode.id);
    setDetail(fresh);
    setForm({
      name: fresh.qode.name,
      purpose: fresh.qode.purpose,
      slug: fresh.qode.slug,
      destination: fresh.qode.destination,
    });
  };

  const runAction = async (
    id: ActionId,
    operation: () => Promise<QodeMutationResponse>,
  ) => {
    setAction(id);
    setError("");
    setNotice("");
    try {
      await refreshAfterMutation(await operation());
    } catch (actionError) {
      setError(
        actionError instanceof Error ? actionError.message : "操作未能完成。",
      );
    } finally {
      setAction(null);
    }
  };

  const saveDraft = () =>
    runAction("save", () =>
      updateDraftQode(currentQode.id, {
        name: form.name.trim(),
        purpose: form.purpose.trim(),
        slug: form.slug.trim(),
        destination: form.destination.trim(),
      }),
    );

  const publish = () =>
    runAction("publish", async () => {
      if (isDraft) {
        const saved = await updateDraftQode(currentQode.id, {
          name: form.name.trim(),
          purpose: form.purpose.trim(),
          slug: form.slug.trim(),
          destination: form.destination.trim(),
        });
        onQodeChanged(saved.qode, saved.event);
      }
      return publishQode(
        currentQode.id,
        form.destination.trim(),
        changeReason.trim(),
      );
    });

  const requestPublishApproval = async () => {
    setAction("request-publish");
    setError("");
    setNotice("");
    try {
      let qodeForRequest = currentQode;
      if (isDraft) {
        const saved = await updateDraftQode(currentQode.id, {
          name: form.name.trim(),
          purpose: form.purpose.trim(),
          slug: form.slug.trim(),
          destination: form.destination.trim(),
        });
        qodeForRequest = saved.qode;
        onQodeChanged(saved.qode, saved.event);
        const fresh = await getQodeDetail(saved.qode.id);
        setDetail(fresh);
        setForm({
          name: fresh.qode.name,
          purpose: fresh.qode.purpose,
          slug: fresh.qode.slug,
          destination: fresh.qode.destination,
        });
      }
      await requestQodeApproval(qodeForRequest.id, {
        actionType: "publish",
        reason: changeReason.trim(),
        ...(!isDraft && destinationChanged
          ? { destination: form.destination.trim() }
          : {}),
      });
      setRequestedActions((current) => new Set(current).add("publish"));
      setNotice("发布审批已提交，等待其他治理成员处理。");
    } catch (actionError) {
      setError(
        actionError instanceof Error ? actionError.message : "审批提交失败。",
      );
    } finally {
      setAction(null);
    }
  };

  const requestRollbackApproval = async (releaseId: string) => {
    setAction(`request-rollback:${releaseId}`);
    setError("");
    setNotice("");
    try {
      await requestQodeApproval(currentQode.id, {
        actionType: "rollback",
        releaseId,
        reason: changeReason.trim(),
      });
      setRequestedActions((current) =>
        new Set(current).add(`rollback:${releaseId}`),
      );
      setNotice("回滚审批已提交，当前解析版本保持不变。");
    } catch (actionError) {
      setError(
        actionError instanceof Error ? actionError.message : "审批提交失败。",
      );
    } finally {
      setAction(null);
    }
  };

  const runVersionAction = async (
    id: ActionId,
    operation: () => Promise<void>,
    successNotice: string,
  ) => {
    setAction(id);
    setError("");
    setNotice("");
    try {
      await operation();
      const fresh = await getQodeDetail(currentQode.id);
      setDetail(fresh);
      setForm({
        name: fresh.qode.name,
        purpose: fresh.qode.purpose,
        slug: fresh.qode.slug,
        destination: fresh.qode.destination,
      });
      setNotice(successNotice);
    } catch (actionError) {
      setError(
        actionError instanceof Error
          ? actionError.message
          : "版本操作未能完成。",
      );
    } finally {
      setAction(null);
    }
  };

  return (
    <div className="qode-drawer-layer">
      <button
        className="qode-drawer-scrim"
        type="button"
        aria-label="关闭 Qode 详情"
        onClick={onClose}
      />
      <aside className="qode-drawer" aria-label={`${currentQode.name} 治理详情`}>
        <header className="qode-drawer-header">
          <div className="drawer-identity">
            <QrPreview value={absoluteShortUrl(currentQode.shortUrl)} size={58} />
            <div>
              <span>{currentQode.id}</span>
              <h2>{currentQode.name}</h2>
              <small>{currentQode.shortUrl}</small>
            </div>
          </div>
          <div className="drawer-header-actions">
            <button
              type="button"
              title="下载 SVG"
              onClick={() =>
                void downloadQrArtifact(
                  absoluteShortUrl(currentQode.shortUrl),
                  "svg",
                  currentQode.slug,
                )
              }
            >
              <Download size={16} />
              SVG
            </button>
            <button
              type="button"
              title="下载 PNG"
              onClick={() =>
                void downloadQrArtifact(
                  absoluteShortUrl(currentQode.shortUrl),
                  "png",
                  currentQode.slug,
                )
              }
            >
              <Download size={16} />
              PNG
            </button>
            <button type="button" aria-label="关闭" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </header>

        <div className="qode-drawer-body">
          {loading ? (
            <div className="drawer-loading">
              <LoaderCircle className="spin" size={18} />
              正在读取治理状态
            </div>
          ) : null}

          {error ? <div className="action-error">{error}</div> : null}
          {notice ? <div className="action-notice">{notice}</div> : null}

          {!loading && detail ? (
            <>
              <section className="drawer-runtime">
                <div>
                  <span className={`status status-${currentQode.status}`}>
                    <i />
                    {currentQode.status === "active"
                      ? "正常解析"
                      : currentQode.status === "degraded"
                        ? "需要处理"
                        : currentQode.status === "draft"
                          ? "尚未发布"
                          : "已暂停"}
                  </span>
                  <small>{currentQode.scans.toLocaleString()} 次累计扫描</small>
                </div>
                {currentQode.status === "active" ||
                currentQode.status === "degraded" ? (
                  <a
                    href={absoluteShortUrl(currentQode.shortUrl)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    测试入口
                    <ArrowUpRight size={14} />
                  </a>
                ) : null}
              </section>

              <section className="drawer-section">
                <div className="drawer-section-title">
                  <div>
                    <h3>QodeVersion 生命周期</h3>
                    <span>
                      Draft → Validated → Ready → Active，每次发布都有独立版本。
                    </span>
                  </div>
                  <GitBranch size={17} />
                </div>
                <div className="version-toolbar">
                  <span>使用入口配置中的目标地址创建下一版本</span>
                  {capabilities.operate ? (
                    <button
                      type="button"
                      disabled={action !== null || !form.destination.trim()}
                      onClick={() =>
                        void runVersionAction(
                          "version-create",
                          async () => {
                            await createQodeVersion(
                              currentQode.id,
                              form.destination.trim(),
                            );
                          },
                          "新的 Draft 版本已创建。",
                        )
                      }
                    >
                      {action === "version-create" ? (
                        <LoaderCircle className="spin" size={14} />
                      ) : (
                        <GitBranch size={14} />
                      )}
                      创建版本
                    </button>
                  ) : null}
                </div>
                {detail.versions.length ? (
                  <div className="version-list">
                    {detail.versions.map((version) => {
                      const actionId =
                        version.status === "draft"
                          ? (`version-validate:${version.id}` as const)
                          : version.status === "validated"
                            ? (`version-ready:${version.id}` as const)
                            : (`version-publish:${version.id}` as const);
                      const canAct =
                        capabilities.operate &&
                        (version.status === "draft" ||
                          version.status === "validated" ||
                          (version.status === "ready" &&
                            capabilities.approve));
                      return (
                        <article className="version-row" key={version.id}>
                          <div className="version-number">
                            <strong>v{version.version}</strong>
                            <span className={`version-state ${version.status}`}>
                              {versionStatusLabel[version.status]}
                            </span>
                          </div>
                          <div className="version-copy">
                            <strong>{version.destination}</strong>
                            <span>
                              {version.validation.valid
                                ? `${version.validation.checks?.length ?? 0} 项验证通过`
                                : "等待验证"}
                            </span>
                          </div>
                          {canAct ? (
                            <button
                              type="button"
                              disabled={action !== null}
                              onClick={() =>
                                void runVersionAction(
                                  actionId,
                                  async () => {
                                    if (version.status === "draft") {
                                      await validateQodeVersion(version.id);
                                    } else if (
                                      version.status === "validated"
                                    ) {
                                      await markQodeVersionReady(version.id);
                                    } else {
                                      const response =
                                        await publishQodeVersion(
                                          version.id,
                                          changeReason.trim(),
                                        );
                                      onQodeChanged(
                                        response.qode,
                                        response.event,
                                      );
                                    }
                                  },
                                  version.status === "draft"
                                    ? "版本验证已通过。"
                                    : version.status === "validated"
                                      ? "版本已进入 Ready 状态。"
                                      : "版本已发布并成为 Active。",
                                )
                              }
                            >
                              {action === actionId ? (
                                <LoaderCircle className="spin" size={14} />
                              ) : version.status === "ready" ? (
                                <Rocket size={14} />
                              ) : (
                                <Check size={14} />
                              )}
                              {version.status === "draft"
                                ? "验证"
                                : version.status === "validated"
                                  ? "标记 Ready"
                                  : "发布"}
                            </button>
                          ) : version.status === "ready" &&
                            !capabilities.approve ? (
                            <small className="version-permission">
                              等待审批成员发布
                            </small>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <p className="drawer-empty">尚未创建显式 QodeVersion。</p>
                )}
              </section>

              <section className="drawer-section">
                <div className="drawer-section-title">
                  <div>
                    <h3>Deployment 批次</h3>
                    <span>
                      {detail.deployments.length} 个批次 ·{" "}
                      {currentQode.deployments.toLocaleString("zh-CN")} 个实体入口
                    </span>
                  </div>
                  <Boxes size={17} />
                </div>
                {detail.deployments.length ? (
                  <div className="drawer-deployment-list">
                    {detail.deployments.map((deployment) => (
                      <article key={deployment.id}>
                        <div>
                          <strong>{deployment.name}</strong>
                          <span>
                            {deployment.mediumType} · {deployment.location}
                          </span>
                        </div>
                        <div>
                          <strong>
                            {deployment.quantity.toLocaleString("zh-CN")}
                          </strong>
                          <code>{deployment.batchReference}</code>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="drawer-empty">尚未登记实体投放批次。</p>
                )}
              </section>

              <section className="drawer-section">
                <div className="drawer-section-title">
                  <div>
                    <h3>入口配置</h3>
                    <span>
                      {isDraft
                        ? "草稿字段可以直接保存"
                        : "已印刷短路径保持不变，目标更新会生成新 Release"}
                    </span>
                  </div>
                  <ShieldCheck size={17} />
                </div>

                <div className="qode-config-form">
                  <label>
                    名称
                    <input
                      value={form.name}
                      disabled={!isDraft || !capabilities.operate}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          name: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    短路径
                    <div className="slug-input">
                      <span>/r/</span>
                      <input
                        value={form.slug}
                        disabled={!isDraft || !capabilities.operate}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            slug: event.target.value,
                          }))
                        }
                      />
                    </div>
                  </label>
                  <label className="form-wide">
                    目标地址
                    <input
                      type="url"
                      value={form.destination}
                      disabled={!canEditConfig}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          destination: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="form-wide">
                    运行目的
                    <textarea
                      rows={3}
                      value={form.purpose}
                      disabled={!isDraft || !capabilities.operate}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          purpose: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="form-wide">
                    变更原因
                    <input
                      value={changeReason}
                      disabled={!capabilities.operate}
                      maxLength={300}
                      placeholder="记录本次发布、恢复或回滚的业务原因"
                      onChange={(event) => setChangeReason(event.target.value)}
                    />
                  </label>
                </div>

                <div className="drawer-actions">
                  {isDraft && capabilities.operate ? (
                    <button
                      className="secondary-button"
                      type="button"
                      disabled={action !== null}
                      onClick={saveDraft}
                    >
                      {action === "save" ? (
                        <LoaderCircle className="spin" size={15} />
                      ) : (
                        <Save size={15} />
                      )}
                      保存草稿
                    </button>
                  ) : null}
                  {!isPaused && capabilities.approve ? (
                    <button
                      className="primary-button"
                      type="button"
                      disabled={
                        action !== null ||
                        requestedActions.has("publish") ||
                        (!isDraft && !destinationChanged)
                      }
                      onClick={publish}
                    >
                      {action === "publish" ? (
                        <LoaderCircle className="spin" size={15} />
                      ) : (
                        <Rocket size={15} />
                      )}
                      {isDraft ? "确认发布" : "发布新目标"}
                    </button>
                  ) : null}
                  {!isPaused && !capabilities.approve && capabilities.operate ? (
                    <button
                      className="primary-button"
                      type="button"
                      disabled={
                        action !== null || (!isDraft && !destinationChanged)
                      }
                      onClick={() => void requestPublishApproval()}
                    >
                      {action === "request-publish" ? (
                        <LoaderCircle className="spin" size={15} />
                      ) : (
                        <ShieldCheck size={15} />
                      )}
                      {requestedActions.has("publish")
                        ? "等待审批"
                        : "提交发布审批"}
                    </button>
                  ) : null}
                  {isPaused && capabilities.operate ? (
                    <button
                      className="primary-button"
                      type="button"
                      disabled={
                        action !== null ||
                        (destinationChanged &&
                          requestedActions.has("publish"))
                      }
                      onClick={() =>
                        destinationChanged
                          ? capabilities.approve
                            ? publish()
                            : void requestPublishApproval()
                          : runAction("resume", () =>
                              resumeQode(currentQode.id),
                            )
                      }
                    >
                      {action === "resume" ||
                      action === "publish" ||
                      action === "request-publish" ? (
                        <LoaderCircle className="spin" size={15} />
                      ) : (
                        <Play size={15} />
                      )}
                      {destinationChanged
                        ? capabilities.approve
                          ? "以新目标恢复"
                          : requestedActions.has("publish")
                            ? "等待审批"
                            : "提交恢复审批"
                        : "恢复入口"}
                    </button>
                  ) : null}
                  {!isDraft && !isPaused && capabilities.operate ? (
                    <button
                      className="danger-button"
                      type="button"
                      disabled={action !== null}
                      onClick={() =>
                        runAction("pause", () => pauseQode(currentQode.id))
                      }
                    >
                      {action === "pause" ? (
                        <LoaderCircle className="spin" size={15} />
                      ) : (
                        <Pause size={15} />
                      )}
                      暂停解析
                    </button>
                  ) : null}
                </div>
              </section>

              <section className="drawer-section">
                <div className="drawer-section-title">
                  <div>
                    <h3>Release 历史</h3>
                    <span>回滚会创建新版本，历史记录保持不可变</span>
                  </div>
                  <History size={17} />
                </div>
                {detail.releases.length ? (
                  <div className="release-list">
                    {detail.releases.map((release) => (
                      <article className="release-row" key={release.id}>
                        <div className="release-version">
                          <span>v{release.version}</span>
                          {release.status === "active" ? (
                            <small>
                              <Check size={12} />
                              当前
                            </small>
                          ) : null}
                        </div>
                        <div className="release-copy">
                          <strong>{release.destination}</strong>
                          <span>{formatRelativeTime(release.activatedAt)}</span>
                          {release.changeReason ? (
                            <small>{release.changeReason}</small>
                          ) : null}
                          {release.approvalRequestId ? (
                            <code>
                              审批 {release.approvalRequestId.slice(0, 12)}
                            </code>
                          ) : null}
                        </div>
                        {release.status !== "active" &&
                        capabilities.operate ? (
                          <button
                            type="button"
                            disabled={
                              action !== null ||
                              requestedActions.has(`rollback:${release.id}`)
                            }
                            onClick={() =>
                              capabilities.approve
                                ? runAction(`rollback:${release.id}`, () =>
                                    rollbackQode(
                                      currentQode.id,
                                      release.id,
                                      changeReason.trim(),
                                    ),
                                  )
                                : void requestRollbackApproval(release.id)
                            }
                          >
                            {action === `rollback:${release.id}` ||
                            action === `request-rollback:${release.id}` ? (
                              <LoaderCircle className="spin" size={14} />
                            ) : (
                              <RotateCcw size={14} />
                            )}
                            {capabilities.approve
                              ? "回滚"
                              : requestedActions.has(`rollback:${release.id}`)
                                ? "等待审批"
                                : "提交回滚审批"}
                          </button>
                        ) : null}
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="drawer-empty">发布后将在这里形成版本历史。</p>
                )}
              </section>

              <section className="drawer-section">
                <div className="drawer-section-title">
                  <div>
                    <h3>最近操作</h3>
                    <span>来自不可修改的审计事件</span>
                  </div>
                </div>
                <div className="drawer-event-list">
                  {detail.events.slice(0, 5).map((event) => (
                    <div key={event.id}>
                      <i className={`timeline-dot timeline-${event.tone}`} />
                      <span>
                        <strong>{event.title}</strong>
                        <small>{formatRelativeTime(event.time)}</small>
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );
}
